/**
 * Helper function to convert HSL to RGB
 */
function hslToRgb(h: number, s: number, l: number): number[] {
  let r: number;
  let g: number;
  let b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return [r, g, b];
}

/**
 * Convert any string to a consistent hex color
 *
 * How it works:
 * - Hashes the input string to a number
 * - Converts hash to HSL (Hue: 0-360°, Saturation: 65-85%, Lightness: 45-60%)
 * - Returns 6-character hex color code (e.g., "4a90e2")
 * - Same text = same color (deterministic)
 * - Default: "4a90e2" (blue) if empty
 */
export function stringToColor(str: string): string {
  if (!str) return '4a90e2'; // Default blue if no string

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to positive number and to hex
  const positiveHash = Math.abs(hash);

  // Generate a color in the middle brightness range (not too dark, not too light)
  const hue = positiveHash % 360;
  const saturation = 65 + (positiveHash % 20); // 65-85% saturation
  const lightness = 45 + (positiveHash % 15);  // 45-60% lightness

  // Convert HSL to hex
  const rgb = hslToRgb(hue / 360, saturation / 100, lightness / 100);
  const hex = rgb.map(x => {
    const hexVal = Math.round(x * 255).toString(16);
    return hexVal.padStart(2, '0');
  }).join('');

  return hex;
}
